import {endPoints, apiServiceHeadersConfig} from "../constants/config";
import qs from "qs";
import axiosInstance from "./axiosInstance";
import {initToast} from "./serverErrorHandler";
import {RsaHelper} from "../features/SecureCommunication/RsaHelper";
import StSecureCommSession from "../features/SecureCommunication";
import {StCryptoASymmetricOperator} from "../features/SecureCommunication/StCryptoASymmetricOperator";

export const getAuthTokenApi = async (username: string, password: string) => {
    try {
        const data = qs.stringify({
            username: username,
            password: password,
            grant_type: "password",
        });
        const config = {
            headers: {
                "Access-Control-Allow-Origin": "*",
                "Content-Type": "application/x-www-form-urlencoded"
            },
        };

        const response = await axiosInstance.post(
            endPoints.getAuthTokenApi,
            data,
            config
        );

        return response?.data;
    } catch (error: any) {
        initToast(error);
        console.error("Error occurred:", error.message);
    }
};
export const pnStatusCheckApi = async (tsnId: string) => {
    const response = await axiosInstance.get(endPoints.pnTsnStatus(tsnId));
    const res = response.data;
    console.log("RES_STATUS", res);
    return res;
};

export const fetchPortfolio = async (
    mfaPayload?: any,
    location?: { source?: any; latitude?: number; longitude?: number } | null
) => {
    try {
        if (location) {
            location.source = undefined;
        }
        const config = {
            headers: {
                ...(mfaPayload && {
                    [apiServiceHeadersConfig.mfaChallengePayload]: JSON.stringify(mfaPayload),
                }),
                [apiServiceHeadersConfig.symmetricKey]: sessionStorage.getItem(apiServiceHeadersConfig.symmetricKey) ?? "",
                [apiServiceHeadersConfig.latitude]: JSON.stringify(location?.latitude ?? 0.0),
                [apiServiceHeadersConfig.longitude]: JSON.stringify(location?.longitude ?? 0.0),
                [apiServiceHeadersConfig.ektarOrgId]: "1",
            },
        };
        return await axiosInstance.get(endPoints.portfolioApi, config);

    } catch (error: any) {
        if (error.response.data) {
            throw error.response.data.errorCode;
        }
        console.error("Error fetching portfolio:", error);
        throw error;
    }
};

export const fetchBeneficiaries = async (type: string) => {
    try {
        const response = await axiosInstance.get(
            endPoints.myBeneficiariesApi(0, 15, type)
        );
        return response.data;
    } catch (error) {
        console.error("Error fetching beneficiaries:", error);
        throw error;
    }
};

export const transferFunds = async (
    type: string,
    payload: object,
    mfaPayload?: object,
    location?: { source?: any; latitude?: number; longitude?: number } | null
) => {
    try {
        if (location) {
            location.source = undefined;
        }
        const config = {
            headers: {
                "Content-Type": "application/json",
                ...(mfaPayload && {
                    [apiServiceHeadersConfig.mfaChallengePayload]: JSON.stringify(mfaPayload),
                }),
                [apiServiceHeadersConfig.latitude]: JSON.stringify(location?.latitude ?? 0.0),
                [apiServiceHeadersConfig.longitude]: JSON.stringify(location?.longitude ?? 0.0),
            },
        };

        return await axiosInstance.post(
            (endPoints as any)[type],
            payload,
            config
        );
    } catch (error) {
        console.error("Error in transfer:", error);
        throw error;
    }
};

export const refreshAccessToken = async () => {
    try {
        const user = localStorage.getItem("user");
        if (!user) throw new Error("User not found in local storage");

        const {refresh_token: refreshToken} = JSON.parse(user);
        const data = qs.stringify({
            grant_type: "refresh_token",
            refresh_token: refreshToken,
        });

        const config = {
            headers: {
                "Access-Control-Allow-Origin": "*",
                "Content-Type": "application/x-www-form-urlencoded",
                Authorization: undefined,
            },
        };

        axiosInstance.defaults.headers.common.Authorization = undefined;
        const response = await axiosInstance.post(
            endPoints.getAuthTokenApi,
            data,
            config
        );
        if (!response) {
            initToast("Error refreshing token");
            return;
        }

        const tokenObj = response.data;
        const updatedUser = {
            ...JSON.parse(user),
            access_token: tokenObj?.access_token,
            refresh_token: tokenObj?.refresh_token,
        };

        localStorage.setItem("user", JSON.stringify(updatedUser));
        axiosInstance.defaults.headers.common.Authorization = `Bearer ${tokenObj?.access_token}`;
        return `${tokenObj.token_type} ${tokenObj?.access_token}`;
    } catch (error) {
        initToast("Session expired, please login again");
        sessionStorage.clear();
        localStorage.setItem("user", "null");
        window.location.href = "/auth/login";
    }
};

export const getLatestTransactionList = async (id: string) => {
    const response = await axiosInstance.get(
        endPoints.latestTransactionList(id, 0, 5, undefined, "DESC")
    );
    console.log("latest trans list", response.data);
    return response;
};

export const getRecentTransactionList = async () => {
    return await axiosInstance.get(
        endPoints.recentTransactionList(0, 5)
    );
};

export const getMyRegistrations = async (accessToken?: string) => {
    try {
        const config = {
            headers: {
                [apiServiceHeadersConfig.secureSessionId]: sessionStorage.getItem(apiServiceHeadersConfig.secureSessionId) ?? "xyz",
                [apiServiceHeadersConfig.ektarOrgId]: "1",
            },
        };
        const response = await axiosInstance.get(endPoints.getMyRegistrations, config);
        if (response) {
            return response.data;
        } else {
            throw new Error("Error fetching registrations");
        }
    } catch (error) {
        throw error;
    }
};

export const KeyExchangeApi = async () => {
    try {
        const rsaHelper = new RsaHelper();
        const {publicKey, privateKey} = await rsaHelper.generateKeyPair();

        const useSecureSession = StSecureCommSession.getInstance();
        useSecureSession.saveClientPublicKey(publicKey);
        useSecureSession.saveClientPrivateKey(privateKey);

        const asymmetricOperator = StCryptoASymmetricOperator.getInstance();

        const config = {
            headers: {
                [apiServiceHeadersConfig.ektarOrgId]: "1",
                [apiServiceHeadersConfig.clientPublicKey]: publicKey,
                Authorization: undefined
            },
        };

        console.log(
            "keyExchangeApi() - axios headers before key-exchange api call: ",
            axiosInstance.defaults.headers
        );
        const scKeyExchangeResponse = await axiosInstance.post(endPoints.keyExchange, {}, config);

        let scSessionId = scKeyExchangeResponse.headers[apiServiceHeadersConfig.secureSessionId]; //need to check
        let encryptedSymmetricKeyFromServer = scKeyExchangeResponse.headers[apiServiceHeadersConfig.symmetricKey];

        if (scKeyExchangeResponse.status === 200 && (encryptedSymmetricKeyFromServer === undefined ||
            encryptedSymmetricKeyFromServer === null)) {
            return scKeyExchangeResponse.data.status;
        }

        useSecureSession.setStSessionKey(scSessionId);

        try {
            let decrypted_key_asymmetric = await asymmetricOperator.decrypt(encryptedSymmetricKeyFromServer,
                privateKey);

            axiosInstance.defaults.headers[apiServiceHeadersConfig.secureSessionId] = scSessionId;

            axiosInstance.defaults.headers.common[apiServiceHeadersConfig.secureSessionId] = scSessionId;

            console.log(`keyExchangeApi() - ${apiServiceHeadersConfig.secureSessionId}: `,
                axiosInstance.defaults.headers[apiServiceHeadersConfig.secureSessionId]
            );
            console.log(
                `keyExchangeApi() - ${apiServiceHeadersConfig.secureSessionId} from common: `,
                axiosInstance.defaults.headers.common[apiServiceHeadersConfig.secureSessionId]
            );
            console.log(
                "keyExchangeApi() - axios headers: ",
                axiosInstance.defaults.headers
            );
            console.log(
                "keyExchangeApi() - axios common headers: ",
                axiosInstance.defaults.headers.common
            );

            useSecureSession.setSymmetricKey(decrypted_key_asymmetric);
        } catch (e: any) {
            console.error("Error while encrypting key", e);
        }

        console.log("keyExchangeApi() - Secure Channel creation successful. ScSessionId: ", scSessionId);
        return [
            scKeyExchangeResponse
        ];

    } catch (error) {
        throw error;
    }
};

export const logout = async () => {
    try {
        const user = localStorage.getItem("user");
        const accessToken = user ? JSON.parse(user).access_token : "";
        const payload = {token: accessToken};

        return await axiosInstance.post(endPoints.logOut, payload);
    } catch (error) {
        throw error;
    }
};

export const getQRImage = async () => {
    try {
        let response = await axiosInstance.post(endPoints.getQRImage);
        console.log("response.data", response);

        console.log("response_type", typeof response.data);

        let responseData = {
            image: response.data.qrImageString,
            xQrLoginId: response.headers["x-qr-session-id"],
        };

        console.log("responseHeaders", response.headers);
        return responseData;
    } catch (error) {
        throw error;
    }
};

export const qrLoginStatus = async (qrId: string) => {
    try {
        const response = await axiosInstance.get(endPoints.QrLoginStatus(qrId));
        return response.data;
    } catch (error) {
        throw error;
    }
};
